'use client';

import { Tooltip } from '@common/components';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';

import { UnauthQueryOptions } from '@/api';

interface ContentSectionProps {
  title: string;
  content?: string;
  className?: string;
}

export const ContentSection = ({
  title,
  content,
  className,
}: ContentSectionProps) => {
  const { data } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/policy-dictionary'](),
    select: ({ context }) => context?.policyDictionaryList,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const dictionary = data ?? [];

  if (!content) return null;

  const paragraphs = content.split('\n');

  return (
    <section className="flex flex-col gap-8">
      <h3 className="mb-8 typo-body3">{title}</h3>
      {paragraphs.map((paragraph, index) => {
        const text = paragraph.trimEnd();

        let parts = [{ text, description: '' }];

        dictionary.forEach(({ keyword, description }) => {
          parts = parts.flatMap((part) => {
            if (part.description) return [part];

            const splits = part.text.split(new RegExp(`(${keyword})`, 'g'));

            return splits.map((text) => ({
              text,
              description: text === keyword ? description : '',
            }));
          });
        });

        return (
          <p
            key={index}
            className={cn(
              'ml-8 whitespace-pre-wrap break-keep text-Gray-800 typo-body3',
              className,
            )}
          >
            {parts.map(({ text, description }, index) =>
              description ? (
                <Tooltip
                  key={index}
                  label={
                    <article className="font-400 typo-caption1">
                      <h5 className="mb-2 font-700 typo-caption1">{text}</h5>
                      {description}
                    </article>
                  }
                  triggerClassName="bg-G-100"
                >
                  {text}
                </Tooltip>
              ) : (
                text
              ),
            )}
          </p>
        );
      })}
    </section>
  );
};
